import { type ArrayData } from "~/domains/userData";
import { resolveArrayCharacter } from "~/services/array/resolveArrayCharacter";
import { resolveUserData } from "~/services/session.server/resolveUserData";
import { type Replacements } from "~/util/applyStringReplacement";
import { type StrapiFormComponent } from "~/services/cms/models/formElements/StrapiFormComponent";
import {
  dynamicSelectZodDescription,
  type DynamicOptions,
} from "~/services/validation/dynamicSelect";
import {
  parentSelectFormElement,
  resolveParentOptions,
} from "./buildParentOptions";
import { personName } from "./personName";
import { labelKinderGenerations } from "./labelKinderGenerations";
import { type FlowExtras } from "~/domains/extraLoaderConfiguration";
import { type ArrayConfigClient } from "~/services/array";
import {
  type LoaderExtrasContext,
  type LoaderExtras,
} from "~/services/flow/server/loaderExtras";
import { ElternteilSummary } from "~/domains/nachlass/erbschein/shared/components/ElternteilSummary";
import { KinderSummary } from "~/domains/nachlass/erbschein/shared/components/KinderSummary";

type ErbfolgeArraySummaryData = {
  category: string;
  arrayData: {
    data: ArrayData;
    configuration: ArrayConfigClient;
  };
};

type ErbfolgeLoaderExtraData = {
  formElements: StrapiFormComponent[];
  dynamicOptions: DynamicOptions | undefined;
  arraySummaryData: ErbfolgeArraySummaryData | undefined;
  deceasedPersonName: string | undefined;
};

export const erbfolgeExtras: FlowExtras<ErbfolgeLoaderExtraData> = {
  renderExtraComponents: ({ arraySummaryData, deceasedPersonName }) => {
    return (
      <>
        {arraySummaryData?.category === "elternteile" && (
          <ElternteilSummary
            data={arraySummaryData.arrayData.data}
            configuration={arraySummaryData.arrayData.configuration}
            deceasedPersonName={deceasedPersonName}
          />
        )}
        {arraySummaryData?.category === "kinder" && (
          <KinderSummary
            data={arraySummaryData.arrayData.data}
            configuration={arraySummaryData.arrayData.configuration}
            category={arraySummaryData.category}
            deceasedPersonName={deceasedPersonName}
          />
        )}
      </>
    );
  },
  getDynamicOptions: (loaderData) => loaderData.dynamicOptions,
};

// The dynamic parent-select fields (e.g. "which sibling does this child belong
// to") declare their options at runtime rather than in the CMS, marked by a
// sentinel description on their zod schema.
function dynamicSelectFieldsOf(context: LoaderExtrasContext) {
  const shape =
    (
      context.flowSessionEngine.pageSchema as {
        shape?: Record<string, unknown>;
      }
    )?.shape ?? {};
  return Object.entries(shape).filter(
    ([, fieldSchema]) =>
      (fieldSchema as { description?: string }).description ===
      dynamicSelectZodDescription,
  );
}

// Ancestor list-item names for the current page, so CMS text can reference the
// specific relative the user is inside (e.g. {{kinder#name}} on a grandchild
// page). The static per-flow replacements can't know this because it depends on
// which array indexes are in the URL.
function buildParentNameReplacements(
  context: LoaderExtrasContext,
): Replacements {
  const { fieldNames, arrayInfo } = context.flowSessionEngine;

  const fieldNamesForPage = arrayInfo
    ? [...fieldNames, arrayInfo.name]
    : fieldNames;

  // Array-summary pages have no schema field names of their own, so also take
  // the ancestor names from the array's own name (e.g. kinder#kinder).
  const sourceFields = [
    ...fieldNames,
    ...(arrayInfo?.name.includes("#") ? [arrayInfo.name] : []),
  ];

  const parentNameFields = [
    ...new Set(
      sourceFields
        .filter((fieldName) => fieldName.includes("#"))
        .flatMap((fieldName) => {
          const segments = fieldName.split("#");
          return segments.slice(0, -1).flatMap((_, depth) => {
            const prefix = segments.slice(0, depth + 1).join("#");
            return [`${prefix}#vorname`, `${prefix}#nachname`];
          });
        })
        .filter((fieldName) => !fieldNamesForPage.includes(fieldName)),
    ),
  ];

  if (parentNameFields.length === 0) return {};

  return resolveUserData(
    {
      ...context.flowSessionEngine.prunedUserData,
      pageData: { arrayIndexes: context.arrayIndexes },
    },
    parentNameFields,
  ) as Replacements;
}

function buildDynamicOptions(
  context: LoaderExtrasContext,
): DynamicOptions | undefined {
  const dynamicSelectFields = dynamicSelectFieldsOf(context);
  if (dynamicSelectFields.length === 0) return undefined;

  return Object.fromEntries(
    dynamicSelectFields.map(([fieldName]) => [
      fieldName,
      resolveParentOptions(
        fieldName,
        context.flowSessionEngine.prunedUserData as Record<string, unknown>,
        context.arrayIndexes ?? [],
      ),
    ]),
  );
}

// Parent-select fields that have no CMS form element yet still need to render,
// so give them a fallback element carrying just the (dynamic) options + label.
function buildParentSelectFallbacks(
  context: LoaderExtrasContext,
  formElements: StrapiFormComponent[],
): StrapiFormComponent[] {
  return dynamicSelectFieldsOf(context)
    .filter(
      ([fieldName]) =>
        !formElements.some(
          (element) => "name" in element && element.name === fieldName,
        ),
    )
    .map(([fieldName]) => parentSelectFormElement(fieldName));
}

// The list (of children, siblings, ...) shown on an array overview page. Gated
// on the page actually being an array entry point, matching the flow's own
// reachability rather than a CMS statement field.
function buildArraySummaryData(
  context: LoaderExtrasContext,
): ErbfolgeArraySummaryData | undefined {
  const { arrayInfo } = context.flowSessionEngine;
  if (arrayInfo?.entryPoint === undefined) return undefined;

  const arrayData = (resolveUserData(
    {
      ...context.flowSessionEngine.prunedUserData,
      pageData: { arrayIndexes: context.arrayIndexes },
    },
    [arrayInfo.name],
  )[arrayInfo.name] ?? []) as ArrayData;

  return {
    category: arrayInfo.name,
    arrayData: {
      data: arrayData,
      configuration: {
        url:
          context.flowId +
          resolveArrayCharacter(
            context.flowSessionEngine.stepId,
            context.arrayIndexes ?? [],
            false,
          ),
        initialInputUrl: arrayInfo.entryPoint,
        disableAddButton: false,
      },
    },
  };
}

export const erbfolgeLoaderExtras = {
  buildReplacements: buildParentNameReplacements,
  buildLoaderData: (context) => ({
    formElements: [
      ...context.formElements,
      ...buildParentSelectFallbacks(context, context.formElements),
    ],
    dynamicOptions: buildDynamicOptions(context),
    arraySummaryData: buildArraySummaryData(context),
    deceasedPersonName: personName({
      vorname: (
        context.flowSessionEngine.prunedUserData as {
          verstorbeneVorname?: string;
        }
      ).verstorbeneVorname,
      nachname: (
        context.flowSessionEngine.prunedUserData as {
          verstorbeneNachname?: string;
        }
      ).verstorbeneNachname,
    }),
  }),
  transformAutoSummary: (sections) => labelKinderGenerations(sections),
} satisfies LoaderExtras<ErbfolgeLoaderExtraData>;
